// src/pages/business/sales/SalesHistoryPage.tsx
// Web adaptation of mobile SalesHistoryScreen

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, Package, Calendar, Filter, X,
  ChevronDown, RefreshCw, Loader2, AlertCircle, ShoppingCart
} from 'lucide-react';
import apiClient from '@/services/apiClient';

interface Product {
  id: string;
  name: string;
  sku?: string;
}

interface SaleItem {
  id: string;
  created_at: string;
  total_amount: number;
  buyer_name?: string | null;
  customer_name?: string | null;
  payment_method?: string | null;
  status?: string | null;
  sale_items?: Array<{
    product_id?: string;
    quantity?: number;
    unit_price?: number;
    total_price?: number;
    products?: {
      id: string;
      name: string;
      sku?: string;
    };
  }>;
}

const PERIODS = [
  { label: 'Today', value: 'Today' },
  { label: 'This Week', value: 'This Week' },
  { label: 'This Month', value: 'This Month' },
  { label: 'All Time', value: 'All' },
] as const;

type PeriodValue = (typeof PERIODS)[number]['value'];

const formatDateDistance = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const SalesHistoryPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();

  const [sales, setSales] = useState<SaleItem[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState<PeriodValue>('Today');
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/businesses/products/${businessId}`);
      setProducts(data?.products || data || []);
    } catch (err: any) {
      console.error('Failed to fetch products:', err);
    }
  }, [businessId]);

  const fetchSales = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      let from: string | undefined;
      let to: string | undefined;
      const now = new Date();

      if (period === 'Today') {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        from = start.toISOString();
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString();
      } else if (period === 'This Week') {
        const start = new Date(now);
        const dayOfWeek = now.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        start.setDate(now.getDate() - diff);
        start.setHours(0, 0, 0, 0);
        from = start.toISOString();
      } else if (period === 'This Month') {
        from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      }

      const params: any = { page: p, limit: 50 };
      if (from) params.from = from;
      if (to) params.to = to;
      if (selectedProduct) params.product_id = selectedProduct;

      const query = new URLSearchParams(params).toString();
      const { data } = await apiClient.get(`/businesses/${businessId}/sales?${query}`);
      const fetched: SaleItem[] = data?.sales || [];

      if (fetched.length === 0) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      if (p === 1) setSales(fetched);
      else setSales((prev) => [...prev, ...fetched]);
    } catch (err: any) {
      console.error('Failed to fetch sales:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [businessId, period, selectedProduct]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchSales(1);
    setPage(1);
    setHasMore(true);
  }, [period, selectedProduct, fetchSales]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchSales(1);
  };

  const loadMore = () => {
    if (loading || !hasMore) return;
    const next = page + 1;
    setPage(next);
    fetchSales(next);
  };

  const totals = useMemo(() => {
    let totalAmount = 0;
    let totalItems = 0;
    sales.forEach((s) => {
      totalAmount += Number(s.total_amount || 0);
      (s.sale_items || []).forEach((it) => {
        totalItems += Number(it.quantity || 0);
      });
    });
    return { totalAmount, totalItems, count: sales.length };
  }, [sales]);

  const currentPeriodLabel = PERIODS.find((p) => p.value === period)?.label || 'Today';
  const currentProductLabel = selectedProduct
    ? products.find((p) => p.id === selectedProduct)?.name || 'All Products'
    : 'All Products';

  const handleSelectProduct = (productId: string | null) => {
    setSelectedProduct(productId);
    setShowProductDropdown(false);
  };

  const handleSelectPeriod = (value: PeriodValue) => {
    setPeriod(value);
    setShowPeriodDropdown(false);
  };

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/business/${businessId}/products`)}
              className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <ArrowLeft size={24} style={{ color: '#4F46E5' }} />
            </button>
            <div>
              <p className="font-bold text-lg" style={{ color: '#4F46E5' }}>Sales History</p>
            </div>
          </div>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border transition-colors"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span className="font-semibold text-sm" style={{ color: '#4F46E5' }}>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-4">
            {/* Period Filter */}
            <div className="relative">
              <p className="text-xs font-medium mb-2" style={{ color: '#64748B' }}>Period</p>
              <button
                onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border font-semibold text-sm transition-all"
                style={{
                  borderColor: '#4F46E5',
                  color: '#4F46E5',
                  backgroundColor: showPeriodDropdown ? 'rgba(79,70,229,0.08)' : 'transparent'
                }}
              >
                {currentPeriodLabel}
                <ChevronDown size={16} className={showPeriodDropdown ? 'rotate-180' : ''} />
              </button>

              {showPeriodDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowPeriodDropdown(false)}
                  />
                  <div className="absolute top-full left-0 mt-2 w-48 bg-light-card dark:bg-dark-card rounded-xl shadow-lg border border-light-border dark:border-dark-border z-50 overflow-hidden">
                    {PERIODS.map((p) => (
                      <button
                        key={p.value}
                        onClick={() => handleSelectPeriod(p.value)}
                        className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors flex items-center justify-between
                          ${period === p.value ? 'bg-primary/10' : 'hover:bg-light-bg dark:hover:bg-dark-bg'}
                        `}
                        style={{ color: period === p.value ? '#4F46E5' : '#0F172A' }}
                      >
                        {p.label}
                        {period === p.value && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#4F46E5' }} />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Product Filter */}
            <div className="relative">
              <p className="text-xs font-medium mb-2" style={{ color: '#64748B' }}>Product</p>
              <button
                onClick={() => setShowProductDropdown(!showProductDropdown)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border font-semibold text-sm transition-all max-w-[200px]"
                style={{
                  borderColor: '#4F46E5',
                  color: '#4F46E5',
                  backgroundColor: showProductDropdown ? 'rgba(79,70,229,0.08)' : 'transparent'
                }}
              >
                <span className="truncate">{currentProductLabel}</span>
                <ChevronDown size={16} className={showProductDropdown ? 'rotate-180' : ''} />
              </button>

              {showProductDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowProductDropdown(false)}
                  />
                  <div className="absolute top-full left-0 mt-2 w-64 max-h-80 overflow-y-auto bg-light-card dark:bg-dark-card rounded-xl shadow-lg border border-light-border dark:border-dark-border z-50">
                    <button
                      onClick={() => handleSelectProduct(null)}
                      className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors flex items-center justify-between
                        ${!selectedProduct ? 'bg-primary/10' : 'hover:bg-light-bg dark:hover:bg-dark-bg'}
                      `}
                      style={{ color: !selectedProduct ? '#4F46E5' : '#0F172A' }}
                    >
                      All Products
                      {!selectedProduct && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#4F46E5' }} />}
                    </button>
                    {products.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => handleSelectProduct(product.id)}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center justify-between
                          ${selectedProduct === product.id ? 'bg-primary/10' : 'hover:bg-light-bg dark:hover:bg-dark-bg'}
                        `}
                        style={{ color: selectedProduct === product.id ? '#4F46E5' : '#0F172A' }}
                      >
                        <span className="truncate flex-1">{product.name}</span>
                        {selectedProduct === product.id && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#4F46E5' }} />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Active Filter Chip */}
            {selectedProduct && (
              <div className="flex items-center gap-2 self-end">
                <span
                  className="flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: 'rgba(79,70,229,0.12)', color: '#4F46E5' }}
                >
                  {currentProductLabel}
                  <button onClick={() => handleSelectProduct(null)} className="hover:opacity-70">
                    <X size={14} />
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={16} style={{ color: '#64748B' }} />
              <span className="text-xs" style={{ color: '#64748B' }}>Total Sales</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: '#4F46E5' }}>
              KES {totals.totalAmount.toLocaleString()}
            </p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Package size={16} style={{ color: '#64748B' }} />
              <span className="text-xs" style={{ color: '#64748B' }}>Items Sold</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: '#4F46E5' }}>
              {totals.totalItems}
            </p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingCart size={16} style={{ color: '#64748B' }} />
              <span className="text-xs" style={{ color: '#64748B' }}>Transactions</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: '#4F46E5' }}>
              {totals.count}
            </p>
          </div>
        </div>

        {/* Sales List */}
        {loading && sales.length === 0 ? (
          <div className="flex justify-center py-24">
            <Loader2 size={40} className="animate-spin" style={{ color: '#4F46E5' }} />
          </div>
        ) : sales.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
            <AlertCircle size={48} style={{ color: '#94A3B8' }} />
            <p className="font-semibold text-lg" style={{ color: '#0F172A' }}>No sales found</p>
            <p style={{ color: '#64748B' }}>No sales recorded for this period</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sales.map((sale) => (
              <div
                key={sale.id}
                className="bg-light-card dark:bg-dark-card rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-xl font-bold" style={{ color: '#4F46E5' }}>
                      KES {Number(sale.total_amount || 0).toLocaleString()}
                    </p>
                    <p className="text-sm mt-1" style={{ color: '#64748B' }}>
                      {sale.customer_name || sale.buyer_name || 'Walk-in'} •{' '}
                      {sale.payment_method || '—'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs" style={{ color: '#64748B' }}>
                      {formatDateDistance(sale.created_at)}
                    </p>
                    {sale.status === 'pending_sync' && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold mt-1"
                        style={{ backgroundColor: 'rgba(255,152,0,0.12)', color: '#FF9800' }}
                      >
                        PENDING SYNC
                      </span>
                    )}
                  </div>
                </div>

                {/* Items Preview */}
                {sale.sale_items && sale.sale_items.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {sale.sale_items.slice(0, 3).map((it, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 rounded-lg text-xs"
                        style={{ backgroundColor: 'rgba(79,70,229,0.08)', color: '#64748B' }}
                      >
                        {it.products?.name || `Product ${it.product_id?.slice(0, 6)}`} ×{it.quantity}
                      </span>
                    ))}
                    {sale.sale_items.length > 3 && (
                      <span
                        className="px-2 py-1 rounded-lg text-xs"
                        style={{ backgroundColor: 'rgba(79,70,229,0.08)', color: '#64748B' }}
                      >
                        +{sale.sale_items.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Load More */}
            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2"
                style={{ backgroundColor: 'rgba(79,70,229,0.08)', color: '#4F46E5' }}
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  'Load More'
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesHistoryPage;